import { memo, useCallback } from 'react'
import { ImageOverlay, MapContainer, Marker, useMapEvents } from 'react-leaflet'
import { CRS, LatLngBoundsLiteral, LatLngTuple } from 'leaflet'
import { FloorDetail, Point } from 'types/building'

interface MapContainerComponentProps {
  mapContainerRef: React.RefObject<any>
  floorDetail: FloorDetail | null
  imgBounds: LatLngTuple
  points: Point[]
  currentViewer: any
  isZoomOutMap: boolean
  isMediumScreen: boolean
  handleMarkerClick: (point: any) => void
  radarMaker: React.ReactNode
  getEnhancedPointStyle: (
    point: any,
    selectedPointId: string | undefined,
    isZoomed: boolean,
    showNumbers: boolean
  ) => any
  mapEventHandler: () => any
  showNumbers: boolean
  setIsMapBackgroundHovered: (value: boolean) => void
}

const MapContainerComponent = memo(
  ({
    mapContainerRef,
    floorDetail,
    imgBounds,
    points,
    currentViewer,
    isZoomOutMap,
    isMediumScreen,
    handleMarkerClick,
    radarMaker,
    getEnhancedPointStyle,
    mapEventHandler,
    showNumbers,
    setIsMapBackgroundHovered
  }: MapContainerComponentProps) => {
    function MapEventHandler() {
      useMapEvents({
        ...mapEventHandler()
      })
      return null
    }

    const getMapBounds = useCallback((): LatLngBoundsLiteral => {
      // Provide default bounds if imgBounds is not available
      if (!imgBounds || imgBounds.length < 2) {
        return [[0, 0] as LatLngTuple, [100, 100] as LatLngTuple]
      }
      // Fallback to original logic if imageRealSize is not available
      return [
        [0, 0] as LatLngTuple,
        [imgBounds[0], imgBounds[1]] as LatLngTuple
      ]
    }, [imgBounds])

    // Early return if no map file is available
    if (!floorDetail?.mapFile?.s3Path) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-gray-100">
          <div className="text-gray-500 text-center p-4">
            <div className="text-lg font-medium mb-2">地図が利用できません</div>
          </div>
        </div>
      )
    }

    try {
      const bounds = getMapBounds()

      return (
        <MapContainer
          key={`map-${floorDetail.id}`} // Force re-render on floor change
          ref={mapContainerRef}
          center={[50, 50]}
          zoom={-0.5}
          zoomSnap={0}
          zoomDelta={0.5}
          className="area-map-container !bg-white"
          crs={CRS.Simple}
          zoomControl={false}
          attributionControl={false}
          doubleClickZoom={false}
          wheelPxPerZoomLevel={60}
          wheelDebounceTime={40}
          dragging
          preferCanvas
        >
          {/* Render points as markers */}
          {points?.map((point) => (
            <Marker
              key={`marker-${point.id}-${point.order}`}
              draggable={false}
              zIndexOffset={
                point.id === currentViewer.selectedPoint
                  ? 999999
                  : 10000 + point.order
              }
              position={[point.y, point.x]}
              icon={getEnhancedPointStyle(
                point,
                currentViewer.selectedPoint,
                isZoomOutMap && isMediumScreen,
                showNumbers
              )}
              eventHandlers={{
                click: (e: any) => {
                  if (e?.originalEvent) {
                    e.originalEvent.stopPropagation()
                  }
                  handleMarkerClick(point)
                },
                dblclick: (e: any) => {
                  if (e?.originalEvent) {
                    e.originalEvent.stopPropagation()
                  }
                },
                mouseover: (e: any) => {
                  setIsMapBackgroundHovered(false)
                  const target =
                    (e.originalEvent?.target as HTMLElement) || e.target
                  const iconEl = target?.closest?.(
                    '.point-icon'
                  ) as HTMLElement | null
                  iconEl?.classList.add('hovered')
                },
                mouseout: (e: any) => {
                  const target =
                    (e.originalEvent?.target as HTMLElement) || e.target
                  const iconEl = target?.closest?.(
                    '.point-icon'
                  ) as HTMLElement | null
                  iconEl?.classList.remove('hovered')
                  setIsMapBackgroundHovered(true)
                }
              }}
            />
          ))}
          <MapEventHandler />
          {radarMaker}
          <ImageOverlay
            url={floorDetail.mapFile.s3Path}
            bounds={bounds}
            opacity={1}
            interactive
            crossOrigin="anonymous"
            zIndex={-1}
          />
        </MapContainer>
      )
    } catch (error) {
      console.error('Error rendering map:', error)
      return (
        <div className="w-full h-full flex items-center justify-center bg-red-50">
          <div className="text-red-500 text-center p-4">
            <div className="text-lg font-medium mb-2">エラーが発生しました</div>
            <div className="text-sm">
              地図の読み込み中にエラーが発生しました
            </div>
          </div>
        </div>
      )
    }
  }
)

export default MapContainerComponent
